import type { Socket } from "socket.io-client";
import { getToken } from "@/platform/auth/utils";
import type {
  AgentEventData,
  AgentStatusUpdate,
  MetricsUpdate,
  RunbookExecutionEventData,
  WebSocketEventCallback,
  WebSocketEventMap,
} from "./types";
import type {
  ActivityDto,
  Notification,
  ThreatIntelligenceDto,
} from "@bitsentry-ce/components/types/api.types";

const WEBSOCKET_DISABLED = import.meta.env.VITE_DISABLE_WEBSOCKET === "true";

type ServerToClientEvents = {
  "activity:new": (data: ActivityDto) => void;
  "agent:event": (data: AgentEventData) => void;
  "agent:status": (data: AgentStatusUpdate) => void;
  connected: (data: WebSocketEventMap["connected"]) => void;
  connect: () => void;
  connect_error: (error: Error) => void;
  disconnect: (reason: string) => void;
  error: (error: Error) => void;
  "metrics:update": (data: MetricsUpdate) => void;
  notification: (data: Notification) => void;
  "runbook:execution": (data: RunbookExecutionEventData) => void;
  subscribed: (data: WebSocketEventMap["subscribed"]) => void;
  "threat:new": (data: ThreatIntelligenceDto) => void;
  "vulnerability:created": (
    data: WebSocketEventMap["vulnerability:created"],
  ) => void;
};

type ClientToServerEvents = {
  subscribe: (data: { channels: string[] }) => void;
  unsubscribe: (data: { channels: string[] }) => void;
};

function getBackendUrl(): string {
  const configuredUrl: unknown = import.meta.env.VITE_API_URL;
  if (typeof configuredUrl === "string" && configuredUrl !== "") {
    return configuredUrl;
  }

  return "http://localhost:3000";
}

class WebSocketService {
  private socket: Socket<ServerToClientEvents, ClientToServerEvents> | null =
    null;
  private eventHandlers = new Map<
    keyof WebSocketEventMap,
    Set<WebSocketEventCallback>
  >();
  private isConnecting = false;
  private connectionGeneration = 0;

  async connect(): Promise<void> {
    if (WEBSOCKET_DISABLED) {
      console.log("🔌 WebSocket is temporarily disabled");
      return;
    }

    if (this.socket?.connected === true || this.isConnecting) {
      console.log("⏭️ Skipping connect: already connected or connecting");
      return;
    }

    const token = getToken();
    console.log("🔑 Token available:", token !== null && token !== "");
    if (token === null || token === "") {
      console.warn("❌ No access token available for WebSocket connection");
      return;
    }

    this.isConnecting = true;

    const generation = ++this.connectionGeneration;
    try {
      const { io } = await import("socket.io-client");
      // Logout/reconnect can happen while the transport chunk is loading.
      if (generation !== this.connectionGeneration) return;
      const currentToken = getToken();
      if (currentToken === null || currentToken === "") {
        this.isConnecting = false;
        return;
      }

      this.socket?.disconnect();
      this.socket = io(getBackendUrl(), {
        auth: { token: currentToken },
        transports: ["websocket", "polling"],
        autoConnect: false,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
      });
      this.setupEventListeners();
      this.socket.connect();
    } catch (error: unknown) {
      if (generation !== this.connectionGeneration) return;
      this.isConnecting = false;
      console.error("Failed to load WebSocket transport", error);
    }
  }

  private setupEventListeners() {
    if (this.socket === null) {
      return;
    }

    const socket = this.socket;

    socket.on("connect", () => {
      console.log("✅ WebSocket connected successfully!");
      this.isConnecting = false;

      // Emit connect event to internal handlers
      this.emit("connect", null);

      // Subscribe to default channels
      this.subscribeToChannels([
        "notifications",
        "threats",
        "activity",
        "metrics",
      ]);
    });

    socket.on("disconnect", (reason) => {
      console.warn("❌ WebSocket disconnected:", reason);
      this.isConnecting = false;

      // Emit disconnect event to internal handlers
      this.emit("disconnect", { reason });
    });

    socket.on("error", (error) => {
      console.error("❌ WebSocket error:", error);
      this.isConnecting = false;
    });

    socket.on("connect_error", (error) => {
      console.error("❌ WebSocket connection error:", error.message);
      this.isConnecting = false;
      // Socket.IO handles reconnection automatically via reconnection: true
    });

    // Handle incoming events
    socket.on("notification", (data) => {
      this.emit("notification", data);
    });

    socket.on("vulnerability:created", (data) => {
      this.emit("vulnerability:created", data);
    });

    socket.on("threat:new", (data) => {
      this.emit("threat:new", data);
    });

    socket.on("agent:status", (data) => {
      this.emit("agent:status", data);
    });

    socket.on("agent:event", (data) => {
      this.emit("agent:event", data);
    });

    socket.on("runbook:execution", (data) => {
      this.emit("runbook:execution", data);
    });

    socket.on("activity:new", (data) => {
      this.emit("activity:new", data);
    });

    socket.on("metrics:update", (data) => {
      this.emit("metrics:update", data);
    });

    socket.on("connected", (_data) => {
      // console.log("WebSocket server acknowledgment:", data);
    });

    socket.on("subscribed", (_data) => {
      // console.log("Subscribed to channels:", data);
    });
  }

  disconnect() {
    this.connectionGeneration += 1;
    if (this.socket !== null) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnecting = false;
  }

  reconnect() {
    this.disconnect();
    // Clear the socket instance to force re-initialization with new token
    this.socket = null;
    void this.connect();
  }

  subscribeToChannels(channels: string[]) {
    if (this.socket?.connected !== true) {
      console.warn("Cannot subscribe to channels: WebSocket not connected");
      return;
    }

    this.socket.emit("subscribe", { channels });
  }

  unsubscribeFromChannels(channels: string[]) {
    if (this.socket?.connected !== true) {
      // console.warn("Cannot unsubscribe from channels: WebSocket not connected");
      return;
    }

    this.socket.emit("unsubscribe", { channels });
  }

  on<TEvent extends keyof WebSocketEventMap>(
    event: TEvent,
    callback: WebSocketEventCallback<WebSocketEventMap[TEvent]>,
  ) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    const handlers = this.eventHandlers.get(event);
    if (handlers !== undefined) {
      handlers.add(callback as WebSocketEventCallback);
    }

    // Return unsubscribe function
    return () => {
      const handlers = this.eventHandlers.get(event);
      if (handlers !== undefined) {
        handlers.delete(callback as WebSocketEventCallback);
        if (handlers.size === 0) {
          this.eventHandlers.delete(event);
        }
      }
    };
  }

  off<TEvent extends keyof WebSocketEventMap>(
    event: TEvent,
    callback?: WebSocketEventCallback<WebSocketEventMap[TEvent]>,
  ) {
    if (callback === undefined) {
      // Remove all handlers for this event
      this.eventHandlers.delete(event);
    } else {
      // Remove specific handler
      const handlers = this.eventHandlers.get(event);
      if (handlers !== undefined) {
        handlers.delete(callback as WebSocketEventCallback);
        if (handlers.size === 0) {
          this.eventHandlers.delete(event);
        }
      }
    }
  }

  private emit<TEvent extends keyof WebSocketEventMap>(
    event: TEvent,
    data: WebSocketEventMap[TEvent],
  ) {
    const handlers = this.eventHandlers.get(event);

    if (handlers !== undefined) {
      handlers.forEach((handler) => {
        try {
          handler(data);
        } catch (error) {
          console.error(
            `Error in WebSocket event handler for ${event}:`,
            error,
          );
        }
      });
    }
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  getSocket(): Socket | null {
    return this.socket;
  }
}

// Export singleton instance
export const webSocketService = new WebSocketService();
